// Pure helpers for the Fantasy Fam allocations editor. Kept separate from
// the React component so the arithmetic is unit-testable without a DOM.

// Notional bankroll a "shares" entry is sized against. Fantasy Fam scoring
// runs on return-percentages, not dollar P&L, so this is just a stable
// reference value that maps a share count to a portfolio weight. Matches
// the post-seed free-league starting capital.
export const DEFAULT_BANKROLL_USD = 10_000;

/**
 * sharesToPct — convert a share count + per-share price into a portfolio
 * weight (percent) against a reference bankroll. Returns NaN for any
 * non-finite / non-positive input so callers can detect bad rows.
 */
export function sharesToPct(
	shares: number,
	priceUsd: number,
	bankrollUsd: number = DEFAULT_BANKROLL_USD,
): number {
	if (
		!Number.isFinite(shares) ||
		!Number.isFinite(priceUsd) ||
		!Number.isFinite(bankrollUsd) ||
		shares <= 0 ||
		priceUsd <= 0 ||
		bankrollUsd <= 0
	) {
		return NaN;
	}
	return ((shares * priceUsd) / bankrollUsd) * 100;
}

/**
 * normalizeComposite — scale a raw {symbol: weight} map so the weights sum
 * to exactly 100, rounded to 2 decimals. Any rounding drift is pushed onto
 * the single largest pick so the total lands precisely at 100 (Fantasy Fam
 * scoring requires it). Returns {} for an empty / all-zero input.
 */
export function normalizeComposite(raw: Record<string, number>): Record<string, number> {
	const entries = Object.entries(raw).filter(([, v]) => Number.isFinite(v) && v > 0);
	if (entries.length === 0) return {};

	const sum = entries.reduce((acc, [, v]) => acc + v, 0);
	if (sum <= 0) return {};

	const normalized: Record<string, number> = {};
	for (const [sym, v] of entries) {
		normalized[sym] = Math.round((v / sum) * 10000) / 100;
	}

	// Push the rounding remainder onto the largest pick.
	const normalizedSum = Object.values(normalized).reduce((a, b) => a + b, 0);
	const drift = Math.round((100 - normalizedSum) * 100) / 100;
	if (drift !== 0) {
		const top = Object.entries(normalized).sort((a, b) => b[1] - a[1])[0];
		if (top) {
			normalized[top[0]] = Math.round((normalized[top[0]] + drift) * 100) / 100;
		}
	}
	return normalized;
}
